import { useEffect, useRef, useState } from 'react';
import { scanBarcode, type ScanResult } from './prodpay-api';

// Сканирование штрихкодов: камера (BarcodeDetector, если поддерживается) + ручной ввод.
type BarcodeDetectorLike = { detect: (src: CanvasImageSource) => Promise<{ rawValue: string }[]> };
type WindowBD = { BarcodeDetector?: new (opts?: { formats?: string[] }) => BarcodeDetectorLike };

export function ScanModal({ onClose }: { onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [manual, setManual] = useState('');
  const [log, setLog] = useState<{ ok: boolean; text: string }[]>([]);
  const [cam, setCam] = useState<'off' | 'on' | 'unsupported' | 'denied'>('off');
  const streamRef = useRef<MediaStream | null>(null);
  const lastRef = useRef<string>('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [onClose]);

  function pushResult(r: ScanResult, code: string) {
    if (r.found)
      setLog((l) =>
        [{ ok: true, text: `${r.name} (${code}) → ${r.status} · ${r.action}` }, ...l].slice(0, 8),
      );
    else
      setLog((l) =>
        [{ ok: false, text: `${code}: ${r.message ?? 'не найдено'}` }, ...l].slice(0, 8),
      );
  }

  async function submit(code: string) {
    const c = code.trim();
    if (!c) return;
    const r = await scanBarcode(c).catch(
      () => ({ ok: false, found: false, message: 'ошибка' }) as ScanResult,
    );
    pushResult(r, c);
  }

  async function startCam() {
    const BD = (window as unknown as WindowBD).BarcodeDetector;
    if (!BD) {
      setCam('unsupported');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCam('on');
      const detector = new BD({ formats: ['code_128', 'ean_13', 'qr_code'] });
      const tick = async () => {
        if (!streamRef.current || !videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          const val = codes[0]?.rawValue;
          if (val && val !== lastRef.current) {
            lastRef.current = val;
            await submit(val);
            // Разрешить повторный скан того же кода через паузу — деталь проводится по
            // стадиям (декларация → склад → изделие) сканом одного штрихкода.
            setTimeout(() => {
              if (lastRef.current === val) lastRef.current = '';
            }, 2500);
          }
        } catch {
          /* ignore frame errors */
        }
        if (streamRef.current) setTimeout(tick, 600);
      };
      tick();
    } catch {
      setCam('denied');
    }
  }

  return (
    <div className="ovl" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-h">
          <div className="grow">
            <div className="m-cli">Сканирование штрихкодов</div>
            <div className="m-no">приём по декларации → склад → изделие</div>
          </div>
          <button className="icon-btn close-x" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="mbody">
          {cam === 'on' && <video ref={videoRef} className="scan-video" muted playsInline />}
          {cam === 'off' && (
            <button className="btn sm" onClick={startCam} style={{ marginBottom: 12 }}>
              📷 Включить камеру
            </button>
          )}
          {cam === 'unsupported' && (
            <p className="muted small">
              Камера-сканер не поддерживается этим браузером — используйте ручной ввод (или
              USB-сканер, он вводит код как с клавиатуры).
            </p>
          )}
          {cam === 'denied' && (
            <p className="err small">
              Нет доступа к камере. Используйте ручной ввод или USB-сканер.
            </p>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit(manual);
              setManual('');
            }}
            style={{ display: 'flex', gap: 8, marginTop: 8 }}
          >
            <input
              className="grow"
              placeholder="Штрихкод (ввод вручную / USB-сканер)"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              autoFocus
            />
            <button className="btn sm" type="submit">
              Провести
            </button>
          </form>

          <div className="section-t">Проведено</div>
          {log.length === 0 && <p className="muted small">Отсканируйте или введите штрихкод.</p>}
          {log.map((l, i) => (
            <div key={i} className={'row ' + (l.ok ? '' : '')} style={{ padding: '8px 0' }}>
              <span className={'chip ' + (l.ok ? 'grn' : 'red')}>{l.ok ? '✓' : '✗'}</span>
              <div className="grow small">{l.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
